import { PortfolioEstateGrid } from "@/components/portfolio/all-estate-overview/PortfolioEstateGrid";
import { PortfolioOverviewEmptyState } from "@/components/portfolio/all-estate-overview/PortfolioOverviewEmptyState";
import { PortfolioOverviewHeader } from "@/components/portfolio/all-estate-overview/PortfolioOverviewHeader";
import type { PortfolioOverviewSummary } from "@/components/portfolio/all-estate-overview/types";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";
import { OverviewSurface } from "@/components/ui/OverviewSurface";
import React from "react";
import { AllPortfolioActionButtons } from "@/components/portfolio/all-estate-overview/AllPortfolioActionButtons";

const AllEstateOverview = ({ embedded = false }: { embedded?: boolean }) => {
  const { language } = useLanguage();
  const summaryLabel =
    language === "cs" ? "Souhrn portfolia" : "Portfolio summary";
  const { allEstateList, estateStats, isPredictionView, setSelectedEstate } =
    useEstateContext();

  const estates = allEstateList ?? [];
  const summary = React.useMemo<PortfolioOverviewSummary>(() => {
    let totalIncome = 0;
    let totalExpense = 0;
    let totalCurrentProfit = 0;
    let totalProjectedProfit = 0;
    let totalLastYearProfit = 0;

    Object.values(estateStats).forEach((estate) => {
      totalIncome += estate.currentYearStats.income;
      totalExpense += estate.currentYearStats.expense;
      totalCurrentProfit += estate.currentYearStats.profit;
      totalProjectedProfit += estate.currentYearStats.profitTotal;
      totalLastYearProfit += estate.lastYearStats.profit;
    });

    const estateCount = estates.length;
    const occupiedCount = estates.filter((estate) =>
      estate.status.includes("RENTED"),
    ).length;

    return {
      totalPurchasePrice: estates.reduce(
        (total, estate) => total + (estate.purchasePrice ?? 0),
        0,
      ),
      totalIncome,
      totalExpense,
      totalCurrentProfit,
      totalProjectedProfit,
      totalLastYearProfit,
      estateCount,
      occupiedCount,
      availableCount: estateCount - occupiedCount,
    };
  }, [estateStats, estates]);

  if (!estates.length) {
    return <PortfolioOverviewEmptyState />;
  }

  const displayedProfit = isPredictionView
    ? summary.totalProjectedProfit
    : summary.totalCurrentProfit;

  return (
    <OverviewSurface frame={embedded ? "topBorder" : "card"}>
      <div className="flex items-center justify-between gap-4 px-5 py-2.5 sm:px-6">
        <p className="app-section-eyebrow">{summaryLabel}</p>
        <AllPortfolioActionButtons showTitle={false} />
      </div>

      <PortfolioOverviewHeader
        summary={summary}
        displayedProfit={displayedProfit}
      />

      {estates.length > 1 ? (
        <PortfolioEstateGrid
          estates={estates}
          estateStats={estateStats}
          isPredictionView={isPredictionView}
          onSelectEstate={setSelectedEstate}
        />
      ) : null}
    </OverviewSurface>
  );
};

export default AllEstateOverview;
